
/**
 * @fileoverview Sidebar optimizado para mostrar fuentes de respuesta de Mia (guías y productos).
 * Proporciona una visualización clara y eficiente de las fuentes asociadas a una respuesta de Mia, incluyendo guías y productos, con cierre eficiente y renderizado optimizado.
 * @module MiaSourcesResponseSidebar
 * @category Component - Organism
 * @since 1.0.0
 * @version 2.0.0 - Optimización de renderizado, memoización y documentación JSDoc completa
 */

import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import RightModalHeader from '../../layouts/Modal/RightModalHeader';
import { SIDEBAR_ACTIONS } from '../../../constants/actionsConstants';



/**
 * @typedef {Object} MiaGuide
 * @description Objeto que representa una guía fuente de Mia.
 * @property {string|number} id - Identificador único de la guía.
 * @property {string} type - Tipo de la guía (por ejemplo, 'manual', 'faq').
 * @property {string} title - Título de la guía.
 * @property {string} [url] - URL asociada a la guía (opcional).
 * @property {string} result - Resumen o resultado relevante de la guía.
 */

/**
 * @typedef {Object} MiaProduct
 * @description Objeto que representa un producto fuente de Mia.
 * @property {string|number} id - Identificador único del producto.
 * @property {string} name - Nombre del producto.
 * @property {string} description - Descripción del producto.
 * @property {Array<string>} images - Lista de URLs de imágenes del producto.
 */

/**
 * @typedef {Object} MiaSources
 * @description Estructura de fuentes asociadas a una respuesta de Mia.
 * @property {Array<MiaGuide>} [guides] - Arreglo de guías fuente.
 * @property {Array<MiaProduct>} [products] - Arreglo de productos fuente.
 */

/**
 * Sidebar optimizado para mostrar fuentes de respuesta de Mia (guías y productos).
 *
 * Implementa el patrón de **Organism** con separación estricta de responsabilidades y optimización de renderizado.
 *
 * @component MiaSourcesResponseSidebar
 * @category Component - Organism
 *
 * @param {Object} props - Propiedades del componente
 * @param {MiaSources} props.sources - Fuentes asociadas a la respuesta de Mia
 *
 * @returns {JSX.Element} Sidebar renderizado con fuentes de Mia
 *
 * @example
 * <MiaSourcesResponseSidebar sources={{ guides: [...], products: [...] }} />
 *
 * @see {@link ../../layouts/Modal/RightModalHeader} Para el header del modal
 * @since 1.0.0
 * @version 2.0.0 - Optimización de renderizado y documentación JSDoc
 */
const MiaSourcesResponseSidebar = memo(function MiaSourcesResponseSidebar({ sources }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  /**
   * Maneja el cierre del sidebar.
   * Memoizado para evitar recreación en cada render.
   *
   * @function handleClickOnClose
   * @memberof MiaSourcesResponseSidebar
   * @private
   * @description
   * 1. Despacha la acción de cierre del sidebar.
   * 2. No recibe parámetros ni retorna valor.
   *
   * @example
   * handleClickOnClose();
   */
  const handleClickOnClose = useCallback(() => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  }, [dispatch]);

  /**
   * Memoiza el renderizado de la lista de guías.
   *
   * @function guidesList
   * @memberof MiaSourcesResponseSidebar
   * @private
   * @returns {JSX.Element[]} Lista de elementos JSX para guías
   *
   * @example
   * {guidesList}
   */
  const guidesList = useMemo(() => (
    sources?.guides?.map((guide, index) => (
      <div
        key={`${guide.id}-${index}`}
        className="tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-200 tw-py-6 tw-px-[18px] tw-gap-6 tw-mb-4 tw-text-default"
      >
        <div className="tw-font-semibold tw-mb-2">
          {`${t(`chatbot_ai.sources_modal.${guide.type.toLowerCase()}`)}: ${guide.title}`}
        </div>
        {guide.url && <div>{guide.url}</div>}
        <p className="tw-mb-1">{guide.result}</p>
      </div>
    ))
  ), [sources?.guides, t]);

  /**
   * Memoiza el renderizado de la lista de productos.
   *
   * @function productsList
   * @memberof MiaSourcesResponseSidebar
   * @private
   * @returns {JSX.Element[]} Lista de elementos JSX para productos
   *
   * @example
   * {productsList}
   */
  const productsList = useMemo(() => (
    sources?.products?.map((product, index) => {
      const title = product.name || product.title;
      const description = product.description || product.result?.description;
      const images = product.images || product.result?.images || [];

      return (
        <div
          key={`${product.id}-${index}`}
          className="tw-bg-gray-50 tw-rounded-xl tw-border tw-border-gray-200 tw-py-6 tw-px-[18px] tw-gap-6 tw-mb-4 tw-text-default"
        >
          <div className="tw-font-semibold tw-mb-2">
            {`${t('chatbot_ai.sources_modal.product.title')}: ${title}`}
          </div>
          {description && <div>{description}</div>}
          <div className="tw-mb-1"><strong>{t('chatbot_ai.sources_modal.product.images')}:</strong> {images.length}</div>
        </div>
      );
    })
  ), [sources?.products, t]);

  return (
    <div className="tw-wrapper tw-flex tw-flex-col tw-h-auto tw-max-h-[90vh] tw-overflow-hidden tw-min-w-[340px] tw-w-full sm:tw-w-[420px] md:tw-w-[520px] lg:tw-w-[640px] tw-max-w-[90vw]">
      <RightModalHeader
        title={t('chatbot_ai.sources_modal.title')}
        onClose={handleClickOnClose}
      />
      <div className="tw-p-4 tw-w-full tw-flex-1 tw-h-auto tw-max-h-[70vh] tw-overflow-y-auto">
        {guidesList}
        {productsList}
      </div>
    </div>
  );
});

MiaSourcesResponseSidebar.displayName = "MiaSourcesResponseSidebar";

export default MiaSourcesResponseSidebar;